import { PLATFORM_RECORD_TYPES } from './registry-record-types'

// Which audited record a page shows (ruling 1). Patterns are React Router
// style; a leading * means "under any parent URL" (a route nested under
// `${url}`). Literal beats *, then the deepest match wins. `role` names the
// org permission whose UPDATE lets a record editor read History (phase 4
// ruling 6, core event.RecordScope); without one, only audit-log holders
// see it.
// `platform` (from the type, never written by hand): a record whose table's
// registry org_scope (edxr-cli's audit.tables) is `platform` (a global
// definition no organization owns: apps, system roles, errors, LMS presets)
// or `cross_org` (a login), PLATFORM_RECORD_TYPES. Content serves its History to
// the system audit log's readers only (SYS_AUDIT_LOG, or root; audit-entries
// gate.go, resolveRecord), whatever the reader may edit, so the admin shows
// it to exactly them (history-button.js) and no role opens it. A super admin
// passes every organization scope, so without it every super admin would
// see a History that answers 403.
// `recordOrg`: a staff page's row-scoped record, which can belong to any
// organization. Content serves its History to a reader without
// SYS_AUDIT_LOG only in the reader's current organization (another's
// answers 404, gate.go; a row of no organization is SYS_AUDIT_LOG's alone),
// so the admin needs the record's organization: 'id' (an organization is
// its own), 'page' (the page declares it once it has loaded the record:
// useAuditRecord's organizationID), 'unknown' (nothing the admin reads says
// it: SYS_AUDIT_LOG and root only). An organization's own pages (/org/...)
// show only its records, and need none.
// `except` lists parameter values that name no record (a create form's
// "new"): the URL then shows no History and records no view.
// `tree`: History reads everything under the record by default, with a
// "This level only" switch (content subtree=1; owner 2026-10-08): a node and
// every node below it, with their files and versions.
// Record types the trail records through named events only, on tables whose
// rows it doesn't capture (audit.tables status excluded), so the registry
// snapshot (registry-record-types.js: audited tables) never lists them. AI
// tools telemetry (R2, owner 2026-10-29: "we're missing the history and
// activity stuff for these pages"): staff's moves to the recycle bin,
// restores, permanent deletes and marks of a row are ai_tools.record_*
// events on the row (its table's name), its session or postmortem the
// parent, organization 0 and sys-visible. Content serves such a History to
// the system audit log's readers alone, and only for a type core lists in
// event.SysOnlyRecordTypes (audit-entries gate.go resolveRecord: a type
// neither audited nor sys-only answers 404), so these are platform records
// here.
export const EVENT_RECORD_TYPES = ['ai_tools_sessions', 'ai_tools_postmortems']
// The action content's catalogue must list before it serves such a History:
// core v1.3.141 added these events and made their tables sys-only in one
// release, and a content on an older core answers record= with 404. So the
// History shows from the first content that has them, with no admin release
// (owner 2026-10-29: telemetry History goes out with content's next release).
export const EVENT_RECORD_ACTIONS = { ai_tools_sessions: 'ai_tools.record_deleted', ai_tools_postmortems: 'ai_tools.record_deleted' }
export const eventHistoryServed = (catalogue, type) => {
  const action = EVENT_RECORD_ACTIONS[type]
  return !action || Boolean(catalogue && (catalogue.actions || []).some(a => a.action === action))
}

const r = (path, type, idParam, extra = {}) => ({
  path,
  record: { type, idParam, children: Boolean(extra.children), tree: Boolean(extra.tree) },
  role: extra.role || null,
  platform: PLATFORM_RECORD_TYPES.includes(type) || EVENT_RECORD_TYPES.includes(type),
  recordOrg: extra.recordOrg || null,
  except: extra.except || []
})
const ORG = key => ({ org: key })
const NODE = { children: true, tree: true, role: ORG('NODE') }
const ORG_IS_ID = { recordOrg: 'id' }
const ORG_FROM_PAGE = { recordOrg: 'page' }
const ORG_UNKNOWN = { recordOrg: 'unknown' }

export const RECORD_ROUTES = [
  // A member's History takes its children: their invites and the accounts
  // the partner API linked to them (user_invites and linked_accounts, both
  // parent users / user_id).
  r('/org/user/list/edit/:userID', 'users', 'userID', { children: true, role: ORG('USER') }),
  r('/org/group/list/edit/:groupID', 'groups', 'groupID', { children: true, role: ORG('GROUP') }),
  r('/org/tradeshow/list/edit/:tradeshowID', 'trade_shows', 'tradeshowID', { role: ORG('TRADESHOW') }),
  r('/org/self-register/list/edit/:selfRegisterID', 'self_registers', 'selfRegisterID', { role: ORG('SELF_REGISTER') }),
  r('*/course/:nodeID', 'nodes', 'nodeID', NODE),
  r('*/template/:nodeID', 'nodes', 'nodeID', NODE),
  r('*/library/:nodeID', 'nodes', 'nodeID', NODE),
  r('*/node/:nodeID', 'nodes', 'nodeID', NODE),
  r('*/:assetID/version', 'assets', 'assetID', { children: true, role: ORG('NODE') }),
  r('*/edit/:assetID/version', 'assets', 'assetID', { children: true, role: ORG('NODE') }),
  r('*/asset/:assetID', 'assets', 'assetID', { children: true }),
  // A course's analytics setup (audit phase 6, Task 19): a topic with its
  // analytics, and one analytic, on their setup and report pages.
  r('*/analytic/:nodeID/configure/topic/:analyticTopicID/edit', 'analytic_topics', 'analyticTopicID', { children: true, role: ORG('ANALYTIC') }),
  r('*/analytic/:nodeID/configure/analytic/:analyticID/edit', 'analytics', 'analyticID', { role: ORG('ANALYTIC') }),
  r('*/analytic/:nodeID/report/:topicID/analytic', 'analytic_topics', 'topicID', { children: true, role: ORG('ANALYTIC') }),
  r('*/analytic/:nodeID/report/:topicID/analytic/:analyticID/table', 'analytics', 'analyticID', { role: ORG('ANALYTIC') }),
  r('*/assessment-manage/list/:nodeID/edit/:assessmentID', 'assessments', 'assessmentID', { children: true, role: ORG('ASSESSMENT_MANAGE') }),
  r('*/assessment-manage/list/:nodeID/view/:assessmentID', 'assessments', 'assessmentID', { children: true, role: ORG('ASSESSMENT_MANAGE') }),
  r('/admin/organization/list/edit/:organizationID', 'organizations', 'organizationID', { children: true, ...ORG_IS_ID }),
  // The staff billing page of an organization shows its organization record.
  r('/admin/billing/organizations/:organizationID(\\d+)', 'organizations', 'organizationID', { children: true, ...ORG_IS_ID }),
  r('/admin/sso/connections/:providerID(\\d+)', 'providers', 'providerID', { children: true, ...ORG_FROM_PAGE }),
  // The same workspace opened from an organization's SSO tab.
  r('/admin/organization/list/edit/:organizationID/sso/connections/:providerID(\\d+)', 'providers', 'providerID', { children: true, ...ORG_FROM_PAGE }),
  // The organization's own page for one of its SSO connections.
  r('/org/sso/connections/:providerID(\\d+)', 'providers', 'providerID', { children: true }),
  // A platform sign-in method's screen (edxr-cli 20261029171615_signin_methods:
  // signin_methods rows are platform signin_method records, the kind their
  // id), so its History is the system audit log's.
  r('/admin/signin-methods/:kind', 'signin_method', 'kind'),
  r('/admin/login/list/edit/:loginID', 'logins', 'loginID'),
  r('/admin/login/list/edit/:loginID/organizations/edit/:userID', 'users', 'userID', ORG_FROM_PAGE),
  r('/admin/app/edit/:appID', 'apps', 'appID', { children: true }),
  r('/admin/app/edit/:appID/store/edit/:appStoreID', 'app_stores', 'appStoreID', { children: true, ...ORG_UNKNOWN }),
  r('/admin/app/edit/:appID/store/edit/:appStoreID/version/edit/:appVersionID', 'app_versions', 'appVersionID', { children: true }),
  r('/admin/app/edit/:appID/store/edit/:appStoreID/version/edit/:appVersionID/version/edit/:appBuildVersionID', 'app_build_versions', 'appBuildVersionID'),
  r('/root/role-sys/list/edit/:roleID', 'role_sys', 'roleID', { children: true }),
  r('/root/role-sys-permission/list/edit/:roleSysPermissionID', 'role_sys_permissions', 'roleSysPermissionID'),
  r('/root/sys-admin/list/edit/:loginID', 'logins', 'loginID'),
  // The type is the one the trail records a row as (the registry's
  // record_type), not always the table's name: error_groups rows are
  // error_group records, alert_routes rows alert_route records
  // (record-routes.registry.test.js holds every type to the registry).
  r('/admin/errors/:groupID', 'error_group', 'groupID'),
  // /admin/alert-routing/routes/new is the create form, never a record.
  r('/admin/alert-routing/routes/:routeID', 'alert_route', 'routeID', { except: ['new'], ...ORG_FROM_PAGE }),
  // /admin/alert-routing/targets/new is the create form, never a record.
  r('/admin/alert-routing/targets/:targetID', 'alert_target', 'targetID', { except: ['new'], ...ORG_FROM_PAGE }),
  // A connected workspace (Connect Slack; edxr-cli 20261029182337:
  // channel_connections rows are channel_connection records, org_scope row),
  // its drawer over Alert connections: the drawer names its organization once
  // loaded.
  r('/admin/alert-routing/connections/:connectionID', 'channel_connection', 'connectionID', ORG_FROM_PAGE),
  // The LMS connector (plan Task 6). A connection's API keys are a panel in
  // its drawer (Phase D D7), each key's History on /org/api-keys/:id; a
  // course's integration page is the course; a preset is a platform record only staff manage, so its History
  // is the system audit log's alone (platform). A session is
  // keyed by its launch token, a bearer credential the trail never records
  // (NO_HISTORY below). The paths repeat the routes' `(\d+)` constraints, so
  // /new create forms match nothing.
  r('/org/lms/connections/:connectionId(\\d+)', 'lms_connection', 'connectionId', { role: ORG('LMS') }),
  r('/org/lms/courses/:nodeId(\\d+)/integration', 'nodes', 'nodeId', NODE),
  r('/org/lms/packages/:packageId(\\d+)/build-log', 'lms_package', 'packageId', { role: ORG('LMS') }),
  r('/org/lms/presets/:presetId(\\d+)', 'lms_preset', 'presetId'),
  // An organization's API key (Phase D, plan Task 15): its History is the
  // key's (record type api_key, with its secrets as children), for whoever
  // may change the organization's keys (API_KEY update, core
  // event.RecordScope). Its request drawer (/requests/:requestLogID) is
  // under it.
  r('/org/api-keys/:keyID(\\d+)', 'api_key', 'keyID', { children: true, role: ORG('API_KEY') }),
  // AI tools telemetry (R2): a session's History takes its records' and
  // postmortems' events (their parent is the session), a postmortem's its
  // findings' (EVENT_RECORD_TYPES above).
  r('/admin/ai-tools/sessions/:sessionID(\\d+)', 'ai_tools_sessions', 'sessionID', { children: true }),
  r('/admin/ai-tools/postmortems/:postmortemID(\\d+)', 'ai_tools_postmortems', 'postmortemID', { children: true })
]

// A section's top (content root=): its History is the whole section, every
// node of it in the organization, or with "This level only" its top-level
// nodes. Only the section's own page; its sub-pages (add, recycle bin) show
// none. Not a record: no view is recorded.
export const ROOT_ROUTES = [
  { path: '/org/node-content', root: 'content', title: 'Content' },
  { path: '/org/node-template', root: 'templates', title: 'Templates' },
  { path: '/org/node-library', root: 'library', title: 'Library' }
].map(x => ({ ...x, record: { type: 'nodes', id: `root:${x.root}`, children: true, tree: true }, role: ORG('NODE') }))

// URL shapes that can't name their record: the page calls useAuditRecord.
export const DECLARED = [
  { path: '*/edit/:assetID', file: 'layout/authorized/org/node/common/assets/asset-edit.js' },
  { path: '*/edit/:assetVersionID', file: 'layout/authorized/org/node/common/assets/asset-version-edit.js' },
  { path: '*/:assetVersionID/edit', file: 'layout/authorized/org/node/common/publish/publish-asset-version-edit.js' },
  { path: '*/edit/:nodeCourseVersionID', file: 'layout/authorized/org/node/common/assets/node-course-version-edit.js' }
]

// Routes with a parameter that show no record of their own, and why.
export const NO_HISTORY = [
  // The LMS connector (feat/lms-connector): its connection, key, course,
  // package and preset pages are record routes above. A session is keyed by
  // its launch token, a bearer credential the trail never records.
  { path: '/org/lms/sessions/:sessionRef([\\w-]+)', reason: 'LMS session: named by its ref; its row (keyed by a bearer launch token) is outside the audit registry, and its events show in its own timeline' },
  { path: '/lms/launch/:intent', reason: "the LMS launch window (Phase F): the intent is a bearer credential, never a record's id, and the page is a learner's, with no History" },
  { path: '/admin/alert-routing/runs/:alertID', reason: 'staff alert runs are the outbox, not an audited record' },
  { path: '/admin/alert-routing/connect/:provider', reason: 'OAuth return page, not a record' },
  { path: '/admin/notify/timeline/:traceID', reason: "a notification's delivery timeline: notify's contents tables are not audited (C-3)" },
  { path: '/org/notification-activity/timeline/:traceID', reason: "a notification's delivery timeline: notify's contents tables are not audited (C-3)" },
  { path: '/admin/packages/:name', reason: 'package records live in rapid-api-packages; their audit rows are not wired to the history reader' },
  { path: '/admin/packages/:name/:channel/:version', reason: 'package records live in rapid-api-packages; their audit rows are not wired to the history reader' },
  { path: '*/:id/edit', reason: 'learner save snapshot: save tables are not audited' },
  { path: '*/edit/:id', reason: 'learner save item: save tables are not audited (shape also used by declared pages, which call useAuditRecord)' },
  { path: '*/analytic', reason: "analytics under a course page; the course History covers the page (its topic and analytic pages show their own)" },
  { path: '*/analytic-general/:nodeID', reason: 'analytics report, not a record' },
  { path: '*/analytic-legacy/course/:nodeID', reason: 'analytics report, not a record' },
  { path: '*/analytic/:nodeID', reason: "a course's analytics setup and report lists; each topic and analytic page shows its own History" },
  { path: '*/assessment-manage/list/:nodeID', reason: "list of a course's assessments; the course History covers it" },
  { path: '*/assessment-report/list/:nodeID', reason: 'learner results report, not a record' },
  { path: '*/heatmap/session/:nodeID', reason: 'heatmap viewer, not a record' },
  { path: '*/:assetID/recycle', reason: 'recycle bin of a parent' },
  { path: '*/:nodeID/recycle', reason: 'recycle bin of a parent' },
  { path: '*/add/:nodeID', reason: 'create form' },
  { path: '*/add/:parentID', reason: 'create form' },
  { path: '*/:id/purge', reason: 'purge dialog' },
  { path: '/admin/app/edit/:appID/store/add', reason: 'create form' },
  { path: '/admin/request-log/:requestID?', reason: 'the request log is its own record of requests' },
  { path: '/launch/content/:nodeID', reason: 'hands the session to the web viewer; no page of its own' },
  { path: '/admin/audit-log/:entry(entry)?/:entryID?', reason: 'the audit log itself; an entry opens in its drawer' },
  { path: '/org/audit-log/:entry(entry)?/:entryID?', reason: 'the audit log itself; an entry opens in its drawer' },
  { path: '/admin/organization/list/edit/:organizationID/sso', reason: 'inside the organization page; its History covers it' },
  { path: '/root/analytic-configure', reason: 'platform analytics categories and types (edit and purge pages); those tables are not audited' },
  { path: '/root/role-sys-permission/list/role/:roleSys', reason: 'list filtered by role' },
  { path: '/root/role-sys-permission/list/identifier/:identifier', reason: 'list filtered by permission' },
  { path: '/root/role-sys/list/logins/:roleSys/role', reason: "list of a role's super admins" }
]

// Dialog routes over a list ("delete this?"): the list behind them is the page.
export const DIALOG_PARAMS = new Set(['deleteID', 'purgeID', 'extendID'])
// Signed-out and public areas: no History.
export const EXEMPT_PREFIXES = ['/public/', '/external/', '/open/', '/auth/', '/redirect/', '/download/', '/doc/']

export const shapeOf = p => p.replace(/:[A-Za-z_]+\??/g, ':')

// A parameter may carry the constraint its route declares
// (`:connectionId(\d+)`): that is its pattern, so a create form (`/new`) is
// no record. An unconstrained one takes anything up to the next slash.
const PARAM = /:([A-Za-z_]+)(\((?:[^()\\]|\\.)*\))?\??/g
const literal = text => text.replace(/[.+^${}()|[\]\\]/g, '\\$&')
const compile = path => {
  const star = path.startsWith('*')
  const src = star ? path.slice(1) : path
  let body = ''
  let at = 0
  for (const m of src.matchAll(PARAM)) {
    body += `${literal(src.slice(at, m.index))}(?<${m[1]}>${m[2] ? m[2].slice(1, -1) : '[^/]+'})`
    at = m.index + m[0].length
  }
  return { star, re: new RegExp(`^${star ? '(?:/[^?#]*?)?' : ''}${body}${literal(src.slice(at))}(?=/|$)`) }
}
const COMPILED = RECORD_ROUTES.map(e => ({ e, ...compile(e.path) }))

const decode = v => {
  try {
    return decodeURIComponent(v)
  } catch (e) {
    return null
  }
}

const rootOf = pathname => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  const root = ROOT_ROUTES.find(x => x.path === path)
  return root && { entry: root, type: root.record.type, id: root.record.id, children: true, tree: true, root: root.root, title: root.title }
}

export const matchRecordRoute = pathname => {
  if (!pathname || EXEMPT_PREFIXES.some(x => pathname.startsWith(x))) return null
  const root = rootOf(pathname)
  if (root) return root
  let best = null
  for (const c of COMPILED) {
    const m = c.re.exec(pathname)
    if (!m) continue
    const end = m.index + m[0].length
    const id = m.groups && decode(m.groups[c.e.record.idParam])
    if (!id || c.e.except.includes(id)) continue
    const better = !best || (best.star && !c.star) || (best.star === c.star && end > best.end)
    if (better) best = { entry: c.e, type: c.e.record.type, id, children: c.e.record.children, tree: c.e.record.tree, star: c.star, end }
  }
  return best && { entry: best.entry, type: best.type, id: best.id, children: best.children, tree: best.tree }
}
